import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../styles/ProjectManagementScreen.scss";
import {
  getAuthHeaders,
  getEffectiveAccessLevel,
  getProfile,
  isAdmin,
  isModerator,
  isLeader,
  normalizeAccessLevel,
} from "../utils/auth";
import { apiFetch } from "../utils/api";
import { recordTaskPageVisit } from "../utils/metrics";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const TASK_STATUSES = [
  "Новая",
  "В работе",
  "На проверке",
  "Выполнена",
  "Отклонена",
];
const PROJECT_STATUSES = ["В работе", "Выполнен", "На паузе", "Проект запущен"];
const PROJECT_TYPE_OPTIONS = [
  "Внутренний",
  "Внешний",
  "Исследовательский",
  "Учебный",
  "Сервисный",
];

function normalizeUsername(value) {
  return value.replace(/^@/, "").trim().toLowerCase();
}


export default function ProjectManagementScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const profile = useMemo(() => getProfile(), []);
  const [effectiveAccessLevel, setEffectiveAccessLevel] = useState(
    getEffectiveAccessLevel(profile)
  );
  const isGlobalAdmin = isAdmin(effectiveAccessLevel);
  const isGlobalModerator = isModerator(effectiveAccessLevel);

  const [project, setProject] = useState(null);
  const [projectTitle, setProjectTitle] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [projectStatus, setProjectStatus] = useState("");
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [projectType, setProjectType] = useState("");
  const [isSavingType, setIsSavingType] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [taskEdits, setTaskEdits] = useState({});
  const [reviewNotes, setReviewNotes] = useState({});

  const authHeaders = useMemo(() => getAuthHeaders(), []);

  useEffect(() => {
    recordTaskPageVisit("project_management");
  }, [id]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError("");

    Promise.all([
      apiFetch(`${API_BASE}/projects/${id}`, { headers: authHeaders }),
      apiFetch(`${API_BASE}/tasks?id_project=${id}`, { headers: authHeaders }),
      apiFetch(`${API_BASE}/get_users`, { headers: authHeaders }),
    ])
      .then(async ([projectResponse, tasksResponse, usersResponse]) => {
        if (!projectResponse.ok || !tasksResponse.ok || !usersResponse.ok) {
          throw new Error("failed");
        }
        const projectData = await projectResponse.json();
        const tasksData = await tasksResponse.json();
        const usersData = await usersResponse.json();
        return { projectData, tasksData, usersData };
      })
      .then(({ projectData, tasksData, usersData }) => {
        if (!isActive) {
          return;
        }
        setProject(projectData);
        setProjectTitle(projectData?.title || "");
        setProjectDescription(projectData?.description || "");
        setProjectStatus(projectData?.status || "В работе");
        setProjectType(projectData?.project_type || "");
        setTasks(Array.isArray(tasksData) ? tasksData : []);
        const list = Array.isArray(usersData) ? usersData : [];
        setUsers(list);
        const profileId = profile?.telegram_id;
        const profileUsername = profile?.username
          ? normalizeUsername(profile.username)
          : "";
        const matchedUser = list.find(
          (user) =>
            (profileId && user.telegram_id === profileId) ||
            (profileUsername &&
              normalizeUsername(user.username || "") === profileUsername),
        );
        if (matchedUser) {
          setEffectiveAccessLevel(
            normalizeAccessLevel(matchedUser.level_of_access || "", matchedUser.role || "")
          );
        }
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setError("Не удалось загрузить данные проекта");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [authHeaders, id, profile?.telegram_id, profile?.username]);

  useEffect(() => {
    if (!tasks.length) {
      return;
    }
    const nextEdits = {};
    tasks.forEach((task) => {
      const users = Array.isArray(task.users) ? task.users : [];
      const userNames = users
        .map((user) => normalizeUsername(user.username || ""))
        .filter(Boolean);
      nextEdits[task.id] = {
        title: task.title || "",
        description: task.description || "",
        deadline: task.deadline || "",
        status: task.status || "Новая",
        users: userNames,
        assignAll: false,
        id_project: task.id_project,
      };
    });
    setTaskEdits(nextEdits);
  }, [tasks]);

  const userOptions = useMemo(() => {
    return users
      .filter((user) => user.username)
      .map((user) => ({
        username: user.username,
        fullName:
          user.full_name ||
          [user.first_name, user.last_name].filter(Boolean).join(" "),
        telegramId: user.telegram_id,
      }));
  }, [users]);

  const projectMembers = useMemo(() => {
    return Array.isArray(project?.members) ? project.members : [];
  }, [project]);

  const projectMemberUsernames = useMemo(
    () =>
      projectMembers
        .map((member) => normalizeUsername(member.username || ""))
        .filter(Boolean),
    [projectMembers],
  );

  const projectMemberDirectory = useMemo(() => {
    const directory = {};
    projectMembers.forEach((member) => {
      const key = normalizeUsername(member.username || "");
      if (key) {
        directory[key] = member;
      }
    });
    return directory;
  }, [projectMembers]);

  const memberRole = useMemo(() => {
    const profileUsername = profile?.username
      ? normalizeUsername(profile.username)
      : "";
    if (!profileUsername || !project?.members) {
      return "";
    }
    const member = project.members.find(
      (entry) => normalizeUsername(entry.username || "") === profileUsername,
    );
    return member?.role || "";
  }, [profile, project]);

  const isProjectHelper = useMemo(() => isLeader(memberRole), [memberRole]);

  const canManageProjectTasks = isGlobalAdmin || isGlobalModerator || isProjectHelper;
  const canReviewTasks = canManageProjectTasks;
  const canManageProjectDetails = isGlobalAdmin;
  const canManageProjectStatus = isGlobalAdmin || isGlobalModerator;
  const canManageProjectType = canManageProjectStatus;
  const canDeleteProject = canManageProjectStatus;

  const projectStatusOptions = useMemo(() => {
    if (projectStatus && !PROJECT_STATUSES.includes(projectStatus)) {
      return [projectStatus, ...PROJECT_STATUSES];
    }
    return PROJECT_STATUSES;
  }, [projectStatus]);

  const projectTypeOptions = useMemo(() => {
    if (projectType && !PROJECT_TYPE_OPTIONS.includes(projectType)) {
      return [projectType, ...PROJECT_TYPE_OPTIONS];
    }
    return PROJECT_TYPE_OPTIONS;
  }, [projectType]);

  const reviewTasks = useMemo(
    () => tasks.filter((task) => task.status === "На проверке"),
    [tasks],
  );

  const completedTasks = useMemo(
    () => tasks.filter((task) => task.status === "Выполнена"),
    [tasks],
  );

  const otherTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          task.status !== "На проверке" && task.status !== "Выполнена",
      ),
    [tasks],
  );

  const userDirectory = useMemo(() => {
    const directory = {};
    userOptions.forEach((user) => {
      directory[normalizeUsername(user.username)] = {
        telegramId: user.telegramId,
        fullName: user.fullName,
      };
    });
    return directory;
  }, [userOptions]);

  useEffect(() => {
    if (!tasks.length) {
      return;
    }
    setTaskEdits((prev) => {
      const next = { ...prev };
      tasks.forEach((task) => {
        const edit = next[task.id];
        if (!edit) {
          return;
        }
        const users = Array.isArray(task.users) ? task.users : [];
        const normalized = users
          .map((user) => normalizeUsername(user.username || ""))
          .filter(Boolean);
        const uniqueUsers = Array.from(new Set(normalized));
        const assignAll =
          projectMemberUsernames.length > 0 &&
          projectMemberUsernames.every((username) =>
            uniqueUsers.includes(username),
          );
        next[task.id] = {
          ...edit,
          users: uniqueUsers,
          assignAll,
        };
      });
      return next;
    });
  }, [projectMemberUsernames, tasks]);

  const handleTaskEditChange = (taskId, field, value) => {
    setTaskEdits((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
  };

  const handleTaskUsersToggle = (taskId, username) => {
    setTaskEdits((prev) => {
      const current = prev[taskId];
      if (!current) {
        return prev;
      }
      const nextUsers = current.users.includes(username)
        ? current.users.filter((entry) => entry !== username)
        : [...current.users, username];
      const assignAll =
        projectMemberUsernames.length > 0 &&
        projectMemberUsernames.every((entry) => nextUsers.includes(entry));
      return {
        ...prev,
        [taskId]: {
          ...current,
          users: nextUsers,
          assignAll,
        },
      };
    });
  };

  const handleTaskTeamToggle = (taskId, checked) => {
    setTaskEdits((prev) => {
      const current = prev[taskId];
      if (!current) {
        return prev;
      }
      return {
        ...prev,
        [taskId]: {
          ...current,
          users: checked ? projectMemberUsernames : [],
          assignAll: checked,
        },
      };
    });
  };

  const handleTaskUpdate = (taskId) => {
    if (!canManageProjectTasks) {
      return;
    }
    setActionError("");
    const payload = taskEdits[taskId];
    if (!payload) {
      return;
    }
    const selectedUsers = payload.assignAll
      ? projectMemberUsernames
      : payload.users;
    const usersPayload = selectedUsers
      .map((username) => {
        const member = projectMemberDirectory[username];
        const fallback = userDirectory[username];
        return {
          username: member?.username || username,
          full_name: member?.full_name || fallback?.fullName || "",
          role: member?.role || "",
          telegram_id: member?.telegram_id || fallback?.telegramId || "",
        };
      })
      .filter((entry) => entry.username);

    apiFetch(`${API_BASE}/tasks/${taskId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({
        title: payload.title,
        description: payload.description,
        deadline: payload.deadline,
        status: payload.status,
        users: usersPayload,
        id_project: payload.id_project,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
      })
      .catch(() => setActionError("Не удалось сохранить задачу"));
  };

  const handleTaskDelete = (taskId) => {
    if (!canManageProjectTasks) {
      return;
    }
    setActionError("");
    apiFetch(`${API_BASE}/tasks/${taskId}`, {
      method: "DELETE",
      headers: authHeaders,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
      })
      .then(() =>
        apiFetch(`${API_BASE}/tasks?id_project=${id}`, {
          headers: authHeaders,
        }),
      )
      .then((response) => response.json())
      .then((data) => setTasks(Array.isArray(data) ? data : []))
      .catch(() => setActionError("Не удалось удалить задачу"));
  };

  const handleReviewTask = (taskId, approved) => {
    if (!canReviewTasks) {
      return;
    }
    setActionError("");
    const message = reviewNotes[taskId] || "";

    apiFetch(`${API_BASE}/tasks/${taskId}/review`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ approved, message }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return apiFetch(`${API_BASE}/tasks?id_project=${id}`, {
          headers: authHeaders,
        });
      })
      .then((response) => response.json())
      .then((data) => setTasks(Array.isArray(data) ? data : []))
      .catch(() => setActionError("Не удалось подтвердить задачу"));
  };

  const handleProjectStatusSave = () => {
    if (!canManageProjectStatus) {
      return;
    }
    const nextStatus = projectStatus.trim();
    if (!nextStatus) {
      setActionError("Укажите статус проекта");
      return;
    }
    setActionError("");
    setIsSavingStatus(true);

    apiFetch(`${API_BASE}/projects/${id}/status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ status: nextStatus }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        setProject((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      })
      .catch(() => setActionError("Не удалось обновить статус проекта"))
      .finally(() => setIsSavingStatus(false));
  };

  const handleProjectTypeSave = () => {
    if (!canManageProjectType) {
      return;
    }
    const nextType = projectType.trim();
    setActionError("");
    setIsSavingType(true);

    apiFetch(`${API_BASE}/projects/${id}/type`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ project_type: nextType }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        setProject((prev) => (prev ? { ...prev, project_type: nextType } : prev));
      })
      .catch(() => setActionError("Не удалось обновить тип проекта"))
      .finally(() => setIsSavingType(false));
  };

  const handleProjectTitleSave = () => {
    if (!canManageProjectDetails || isSavingTitle) {
      return;
    }
    const nextTitle = projectTitle.trim();
    if (!nextTitle) {
      setActionError("Укажите название проекта");
      return;
    }
    setActionError("");
    setIsSavingTitle(true);

    apiFetch(`${API_BASE}/projects/${id}/title`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ title: nextTitle }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        setProject((prev) => (prev ? { ...prev, title: nextTitle } : prev));
        setProjectTitle(nextTitle);
      })
      .catch(() => setActionError("Не удалось обновить название проекта"))
      .finally(() => setIsSavingTitle(false));
  };

  const handleProjectDescriptionSave = () => {
    if (!canManageProjectDetails || isSavingDescription) {
      return;
    }
    const nextDescription = projectDescription.trim();
    setActionError("");
    setIsSavingDescription(true);

    apiFetch(`${API_BASE}/projects/${id}/description`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ description: nextDescription }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        setProject((prev) =>
          prev ? { ...prev, description: nextDescription } : prev,
        );
        setProjectDescription(nextDescription);
      })
      .catch(() => setActionError("Не удалось обновить описание проекта"))
      .finally(() => setIsSavingDescription(false));
  };

  const handleDeleteProjectOpen = () => {
    if (!canDeleteProject) {
      return;
    }
    setActionError("");
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteProjectClose = () => {
    if (isDeletingProject) {
      return;
    }
    setIsDeleteConfirmOpen(false);
  };

  const handleDeleteProjectConfirm = () => {
    if (!canDeleteProject || isDeletingProject) {
      return;
    }
    setIsDeletingProject(true);
    setActionError("");

    const requestDelete = () =>
      apiFetch(`${API_BASE}/projects/${id}`, {
        method: "DELETE",
        headers: authHeaders,
      }).then((response) => {
        if (response.ok) {
          return;
        }
        if (response.status === 405) {
          return apiFetch(`${API_BASE}/projects/${id}/delete`, {
            method: "POST",
            headers: authHeaders,
          }).then((fallbackResponse) => {
            if (!fallbackResponse.ok) {
              throw new Error("failed");
            }
          });
        }
        throw new Error("failed");
      });

    requestDelete()
      .then(() => {
        navigate("/projects");
      })
      .catch(() => {
        setActionError("Не удалось удалить проект");
        setIsDeleteConfirmOpen(false);
      })
      .finally(() => setIsDeletingProject(false));
  };

  const deleteProjectModal = isDeleteConfirmOpen
    ? createPortal(
        <div
          className="project-management__overlay"
          onClick={handleDeleteProjectClose}
        >
          <div
            className="project-management__modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="project-management__modal-eyebrow">Удаление проекта</p>
            <h3 className="project-management__modal-title">Вы уверены?</h3>
            <p className="project-management__modal-text">
              Проект и все задачи будут удалены без возможности восстановления.
            </p>
            <div className="project-management__modal-actions">
              <button
                type="button"
                className="btn"
                onClick={handleDeleteProjectClose}
                disabled={isDeletingProject}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteProjectConfirm}
                disabled={isDeletingProject}
              >
                {isDeletingProject ? "Удаляем..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const renderTaskRow = (task) => {
    const edit = taskEdits[task.id];
    if (!edit) {
      return null;
    }

    return (
      <article className="task-row" key={task.id}>
        <div className="task-row__main">
          <input
            value={edit.title}
            onChange={(event) =>
              handleTaskEditChange(
                task.id,
                "title",
                event.target.value,
              )
            }
            disabled={!canManageProjectTasks}
          />
          <textarea
            value={edit.description}
            onChange={(event) =>
              handleTaskEditChange(
                task.id,
                "description",
                event.target.value,
              )
            }
            rows={2}
            disabled={!canManageProjectTasks}
          />
          <div className="task-row__meta">
            <label>
              Статус
              <select
                value={edit.status}
                onChange={(event) =>
                  handleTaskEditChange(
                    task.id,
                    "status",
                    event.target.value,
                  )
                }
                disabled={!canManageProjectTasks}
              >
                {TASK_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Дедлайн
              <input
                type="date"
                value={edit.deadline}
                onChange={(event) =>
                  handleTaskEditChange(
                    task.id,
                    "deadline",
                    event.target.value,
                  )
                }
                disabled={!canManageProjectTasks}
              />
            </label>
            <div className="task-row__assignees">
              <span>Исполнители</span>
              <div className="task-row__users">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={edit.assignAll}
                    onChange={(event) =>
                      handleTaskTeamToggle(
                        task.id,
                        event.target.checked,
                      )
                    }
                    disabled={!canManageProjectTasks}
                  />
                  <span>Вся команда</span>
                </label>
                <div className="task-row__user-list">
                  {projectMembers.map((member) => {
                    const username = normalizeUsername(
                      member.username || "",
                    );
                    if (!username) {
                      return null;
                    }
                    return (
                      <label
                        className="task-row__user-item"
                        key={member.username || member.full_name}
                      >
                        <input
                          type="checkbox"
                          checked={edit.users.includes(username)}
                          onChange={() =>
                            handleTaskUsersToggle(task.id, username)
                          }
                          disabled={
                            !canManageProjectTasks || edit.assignAll
                          }
                        />
                        <span>
                          {member.full_name || member.username}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          {task.completion_message && (
            <div className="task-row__note">
              <strong>Отчет:</strong> {task.completion_message}
            </div>
          )}
          {task.review_message && (
            <div className="task-row__note">
              <strong>Ответ модератора:</strong> {task.review_message}
            </div>
          )}
        </div>
        <div className="task-row__actions">
          {task.status === "На проверке" && canReviewTasks && (
            <div className="task-row__review">
              <div className="review-group">
                <textarea
                  className="review-textarea"
                  placeholder="Комментарий по проверке"
                  value={reviewNotes[task.id] || ""}
                  onChange={(event) =>
                    setReviewNotes((prev) => ({
                      ...prev,
                      [task.id]: event.target.value,
                    }))
                  }
                  rows={2}
                />
                <div className="comments_btns">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleReviewTask(task.id, true)}
                  >
                    Подтвердить
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleReviewTask(task.id, false)}
                  >
                    Отклонить
                  </button>
                </div>
              </div>
              <div className="task-row__review-actions"></div>
            </div>
          )}
          {canManageProjectTasks && (
            <div className="task-row__manage">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleTaskUpdate(task.id)}
              >
                Сохранить
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleTaskDelete(task.id)}
              >
                Удалить
              </button>
            </div>
          )}
        </div>
      </article>
    );
  };

  if (isLoading) {
    return (
      <section className="project-management">
        <div className="project-management__panel">Загрузка проекта...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="project-management">
        <div className="project-management__panel">{error}</div>
      </section>
    );
  }

  return (
    <section className="project-management">
      <header className="project-management__header">
        <Link className="project-management__back" to={`/projects/${id}`}>
          ←
        </Link>
        <div>
          <p className="project-management__label">Управление проектом</p>
          <h2 className="project-management__title">
            {project?.title || "Проект"}
          </h2>
          <p className="project-management__subtitle">
            {project?.description || "Описание пока не добавлено."}
          </p>
          <p className="project-management__meta-line">
            Тип: {project?.project_type || "Не указан"}
          </p>
        </div>
        <div className="project-management__meta">
          <div className="project-management__role">
            <span>Уровень:</span>
            <strong>
              {isGlobalAdmin
                ? "Админ"
                : isGlobalModerator
                  ? "Модератор"
                  : isProjectHelper
                    ? "Хелпер"
                    : "Участник"}
            </strong>
          </div>
          {canDeleteProject && (
            <button
              type="button"
              className="btn btn-danger project-management__delete"
              onClick={handleDeleteProjectOpen}
            >
              Удалить проект
            </button>
          )}
        </div>
      </header>

      {actionError && (
        <div className="project-management__error">{actionError}</div>
      )}

      <section className="project-management__status project-management__details">
        <div>
          <h3>Название и описание</h3>
          <p>Изменение этих полей доступно только администраторам.</p>
        </div>
        <div className="project-management__details-controls">
          <div className="project-management__details-row">
            <label className="project-management__details-field">
              Название проекта
              <input
                type="text"
                value={projectTitle}
                onChange={(event) => setProjectTitle(event.target.value)}
                disabled={!canManageProjectDetails || isSavingTitle}
                placeholder="Введите название проекта"
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleProjectTitleSave}
              disabled={!canManageProjectDetails || isSavingTitle}
            >
              {isSavingTitle ? "Сохранение..." : "Сохранить название"}
            </button>
          </div>
          <div className="project-management__details-row">
            <label className="project-management__details-field">
              Описание проекта
              <input
                type="text"
                value={projectDescription}
                onChange={(event) => setProjectDescription(event.target.value)}
                disabled={!canManageProjectDetails || isSavingDescription}
                placeholder="Добавьте описание проекта"
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleProjectDescriptionSave}
              disabled={!canManageProjectDetails || isSavingDescription}
            >
              {isSavingDescription ? "Сохранение..." : "Сохранить описание"}
            </button>
          </div>
        </div>
        {!canManageProjectDetails && (
          <p className="project-management__hint">
            Менять название и описание проекта могут только администраторы.
          </p>
        )}
      </section>

      <section className="project-management__status">
        <div>
          <h3>Статус проекта</h3>
          <p>Меняется вручную модератором или администратором.</p>
        </div>
        <div className="project-management__status-controls">
          <select
            value={projectStatus}
            onChange={(event) => setProjectStatus(event.target.value)}
            disabled={!canManageProjectStatus}
          >
            {projectStatusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleProjectStatusSave}
            disabled={!canManageProjectStatus || isSavingStatus}
          >
            {isSavingStatus ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
        {!canManageProjectStatus && (
          <p className="project-management__hint">
            Менять статус проекта могут модераторы и администраторы.
          </p>
        )}
      </section>

      <section className="project-management__status project-management__type">
        <div>
          <h3>Тип проекта</h3>
          <p>Используется для структуры и фильтрации проектного портфеля.</p>
        </div>
        <div className="project-management__status-controls">
          <input
            type="text"
            list="project-type-options"
            value={projectType}
            onChange={(event) => setProjectType(event.target.value)}
            disabled={!canManageProjectType}
            placeholder="Например: Внутренний"
          />
          <datalist id="project-type-options">
            {projectTypeOptions.map((typeOption) => (
              <option key={typeOption} value={typeOption} />
            ))}
          </datalist>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleProjectTypeSave}
            disabled={!canManageProjectType || isSavingType}
          >
            {isSavingType ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
        {!canManageProjectType && (
          <p className="project-management__hint">
            Менять тип проекта могут модераторы и администраторы.
          </p>
        )}
      </section>

      <div className="project-management__layout">
        <section className="project-management__panel">
          <div className="project-management__panel-header">
            <h3>Задачи</h3>
            <div className="project-management__panel-actions">
              <span className="project-management__panel-count">
                {tasks.length}
              </span>
            </div>
          </div>
          {!canManageProjectTasks && (
            <p className="project-management__hint">
              Управлять задачами могут администраторы, модераторы и хелперы проекта.
            </p>
          )}
          <div className="project-management__tasks">
            {tasks.length === 0 && (
              <p className="project-management__hint">
                Задачи пока не добавлены.
              </p>
            )}
            {tasks.length > 0 && (
              <div className="project-management__task-groups">
                <section className="project-management__task-group">
                  <div className="project-management__task-group-header">
                    <h4>На проверке</h4>
                    <span className="project-management__task-group-count">
                      {reviewTasks.length}
                    </span>
                  </div>
                  {reviewTasks.length === 0 ? (
                    <p className="project-management__hint">
                      Задач на проверке нет.
                    </p>
                  ) : (
                    <div className="project-management__task-group-list">
                      {reviewTasks.map(renderTaskRow)}
                    </div>
                  )}
                </section>

                <section className="project-management__task-group">
                  <div className="project-management__task-group-header">
                    <h4>Выполнены</h4>
                    <span className="project-management__task-group-count">
                      {completedTasks.length}
                    </span>
                  </div>
                  {completedTasks.length === 0 ? (
                    <p className="project-management__hint">
                      Выполненных задач нет.
                    </p>
                  ) : (
                    <div className="project-management__task-group-list">
                      {completedTasks.map(renderTaskRow)}
                    </div>
                  )}
                </section>

                {otherTasks.length > 0 && (
                  <section className="project-management__task-group">
                    <div className="project-management__task-group-header">
                      <h4>Остальные</h4>
                      <span className="project-management__task-group-count">
                        {otherTasks.length}
                      </span>
                    </div>
                    <div className="project-management__task-group-list">
                      {otherTasks.map(renderTaskRow)}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {deleteProjectModal}
    </section>
  );
}
